import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAlert } from '../context/AlertContext';
import '../Style/AuditDashboard.css';

const AuditDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const { showSuccess, showError, showConfirm } = useAlert();

  const token = localStorage.getItem('idToken');
  const authHeader = useMemo(() => ({
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  const loadDashboardData = useCallback(async () => {
    try {
      const response = await axios.get('/api/audit/dashboard?days=7', authHeader);
      setDashboardData(response.data.data);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      showError('Failed to load dashboard data');
    }
  }, [authHeader, showError]);

  const loadHealthData = useCallback(async () => {
    try {
      const response = await axios.get('/api/audit/health', authHeader);
      setHealthData(response.data.data);
    } catch (error) {
      console.error('Error loading health data:', error);
      showError('Failed to load health data');
    }
  }, [authHeader, showError]);

  // Service status functionality removed

  useEffect(() => {
    loadDashboardData();
    loadHealthData();
  }, [loadDashboardData, loadHealthData]);

  // Anomaly detection functionality removed

  // On-demand analysis functionality removed

  // Service toggle functionality removed

  const exportAuditData = async (format) => {
    try {
      const response = await axios.post('/api/audit/export', {
        format,
        startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        endDate: new Date().toISOString(),
        includeDetails: true
      }, {
        ...authHeader,
        responseType: format === 'csv' ? 'blob' : 'json'
      });

      if (format === 'csv') {
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `audit-export-${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
        showSuccess('CSV export downloaded');
      } else {
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `audit-export-${new Date().toISOString().split('T')[0]}.json`;
        link.click();
        window.URL.revokeObjectURL(url);
        showSuccess('JSON export downloaded');
      }
    } catch (error) {
      console.error('Error exporting data:', error);
      showError('Failed to export data');
    }
  };

  const resetAuditLogs = async () => {
    // Show severe confirmation modal
    const confirmed = await showConfirm(
      ` CRITICAL WARNING 

This action will PERMANENTLY DELETE ALL AUDIT LOGS from the system. This includes:
• All login/logout records
• All user activity logs
• All security events
• All administrative actions
• All system access records

BEFORE PROCEEDING:
✓ Ensure all audit logs have been properly exported and backed up
✓ Confirm this action is authorized by your security policy
✓ Verify you have saved any required compliance reports

This action CANNOT be undone and will reset the audit trail to zero.

Are you absolutely certain you want to proceed?`,
      ' RESET ALL AUDIT LOGS',
      'YES, DELETE ALL LOGS',
      'Cancel'
    );

    if (!confirmed) {
      return;
    }

    // Second confirmation for extra safety
    const doubleConfirmed = await showConfirm(
      `FINAL CONFIRMATION

You are about to permanently delete ALL audit history.

Type confirmation: Are you sure you want to reset all audit logs?`,
      'Final Confirmation Required',
      'CONFIRM RESET',
      'Cancel'
    );

    if (!doubleConfirmed) {
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post('/api/audit/reset-logs', {}, authHeader);

      // Reload dashboard data to show the reset
      await loadDashboardData();
      await loadHealthData();

      showSuccess(`Audit logs reset successfully. ${response.data.data.deleted_records} records were permanently deleted.`);
    } catch (error) {
      console.error('Error resetting audit logs:', error);
      showError(error.response?.data?.message || 'Failed to reset audit logs');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'HEALTHY': return '#28a745';
      case 'WARNING': return '#ffc107';
      case 'CRITICAL': return '#dc3545';
      default: return '#6c757d';
    }
  };

  // getRiskColor function removed (was used for anomalies)

  if (loading && !dashboardData) {
    return <div className="audit-dashboard loading">Loading audit dashboard...</div>;
  }

  return (
    <div className="audit-dashboard">
      <div className="dashboard-header">
        <h2>Audit & Security Dashboard</h2>
      </div>

      <div className="dashboard-actions">
        <button onClick={() => exportAuditData('csv')} className="btn btn-secondary">
          Export CSV
        </button>
        <button onClick={() => exportAuditData('json')} className="btn btn-secondary">
          Export JSON
        </button>
        <button onClick={resetAuditLogs} className="btn btn-danger">
          Reset All Logs
        </button>
      </div>

      <div className="dashboard-tabs">
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`tab ${activeTab === 'health' ? 'active' : ''}`}
          onClick={() => setActiveTab('health')}
        >
          Health
        </button>
      </div>

      <div className="dashboard-content">
        {activeTab === 'overview' && dashboardData && (
          <div className="overview-tab">
            <div className="summary-cards">
              <div className="summary-card">
                <h3>Total Events</h3>
                <div className="metric-value">{dashboardData.summary.total_events.toLocaleString()}</div>
              </div>
              <div className="summary-card">
                <h3>Success Rate</h3>
                <div className="metric-value">{dashboardData.summary.success_rate}%</div>
              </div>
              <div className="summary-card">
                <h3>Unique Users</h3>
                <div className="metric-value">{dashboardData.summary.unique_users}</div>
              </div>
              <div className="summary-card">
                <h3>Unique IPs</h3>
                <div className="metric-value">{dashboardData.summary.unique_ips}</div>
              </div>
            </div>

            <div className="charts-section">
              <div className="chart-container">
                <h4>Daily Activity Trends</h4>
                <div className="daily-trends">
                  {dashboardData.charts.daily_trends.map((day, index) => (
                    <div key={index} className="trend-item">
                      <div className="trend-date">{day.date}</div>
                      <div className="trend-stats">
                        <span className="events-count">{day.total_events} events</span>
                        <span className={`failures-count ${day.failure_events > 0 ? 'has-failures' : ''}`}>
                          {day.failure_events} failures
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="chart-container">
                <h4>Top Active Users</h4>
                <div className="top-users">
                  {dashboardData.charts.top_users.slice(0, 5).map((user, index) => (
                    <div key={index} className="user-item">
                      <div className="user-email">{user.user_email}</div>
                      <div className="user-stats">
                        <span className="events-count">{user.event_count} events</span>
                        <span className={`failures-count ${user.failure_count > 0 ? 'has-failures' : ''}`}>
                          {user.failure_count} failures
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'health' && healthData && (
          <div className="health-tab">
            <div className="health-summary">
              <h3>System Health: 
                <span 
                  className="health-status" 
                  style={{ color: getStatusColor(healthData.overall_status) }}
                >
                  {healthData.overall_status}
                </span>
              </h3>
            </div>

            <div className="health-metrics">
              {healthData.metrics.map((metric, index) => (
                <div key={index} className="health-metric">
                  <div className="metric-header">
                    <span className="metric-name">{metric.metric_name}</span>
                    <span 
                      className="metric-status"
                      style={{ color: getStatusColor(metric.status) }}
                    >
                      {metric.status}
                    </span>
                  </div>
                  <div className="metric-value">{metric.metric_value}</div>
                  <div className="metric-recommendation">{metric.recommendation}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Anomalies tab removed */}

        {/* Service tab removed */}
      </div>
    </div>
  );
};

export default AuditDashboard;
